"use client";

import type { BlogArticleListItem } from "@tsTypes/blog-schema";
import {
  Center,
  Pagination,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import BlogCard from "./BlogCard";
import classes from "./blog.module.css";

const PAGE_SIZE = 20;

function normalize(s: string) {
  return s.trim().toLowerCase();
}

function BlogPagination({
  total,
  page,
  disabled,
  visible,
  onChange,
}: {
  total: number;
  page: number;
  disabled: boolean;
  visible: boolean;
  onChange: (p: number) => void;
}) {
  if (!visible) return null;
  return (
    <Center>
      <Pagination
        total={total}
        value={page}
        onChange={onChange}
        disabled={disabled}
        color="darkblue"
        size="md"
        radius="md"
        siblings={1}
        boundaries={1}
        hideWithOnePage={false}
      />
    </Center>
  );
}

export default function BlogArticlesSection({
  articles,
}: {
  articles: BlogArticleListItem[];
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 200);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const filtered = useMemo(() => {
    const q = normalize(debouncedQuery);
    if (!q) return articles;
    return articles.filter((a) => {
      const titleMatch = normalize(a.title).includes(q);
      const desc = a.metaDescription ?? "";
      const descMatch = normalize(desc).includes(q);
      return titleMatch || descMatch;
    });
  }, [articles, debouncedQuery]);

  const isSearchActive = normalize(debouncedQuery).length > 0;
  const rawTotalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginationDisabled = filtered.length === 0 && isSearchActive;
  const paginationTotal = paginationDisabled ? 1 : Math.max(1, rawTotalPages);
  const displayPage =
    filtered.length === 0
      ? 1
      : Math.min(Math.max(1, page), rawTotalPages);
  const showPagination = paginationDisabled || rawTotalPages > 1;

  const pageArticles = useMemo(() => {
    const start = (displayPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, displayPage]);

  return (
    <Stack gap="xl" mt={{ base: "3rem", sm: "3.5rem" }}>
        <div className={classes.searchWrapper}>
        <TextInput
        placeholder="Search by title or description…"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        size="md"
        radius="md"
        aria-label="Search blog articles"
        styles={{ input: { maxWidth: 480 } }}
        mx="auto"
        w="100%"
      />
          <BlogPagination
            total={paginationTotal}
            page={displayPage}
            disabled={paginationDisabled}
            visible={showPagination}
            onChange={setPage}
          />
        </div>
          <SimpleGrid
            className={clsx("progressive-fade-in")}
            cols={{ base: 1, sm: 2, lg: 3 }}
            spacing={{ base: "2rem", sm: "2.25rem", lg: "2.75rem" }}
          >
           {filtered.length === 0 ? (
        <Text ta="center" c="dimmed" py="xl">
          No articles match your search.
        </Text>
      ) : (
            pageArticles.map((a) => (
              <BlogCard key={a.documentId} article={a} />
            )))}
          </SimpleGrid>
            <BlogPagination
              total={paginationTotal}
              page={displayPage}
              disabled={paginationDisabled}
              visible={showPagination}
              onChange={setPage}
            />
    </Stack>
  );
}
